import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { Divider, makeStyles, Popover, Tooltip } from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import { MEASURE_SECTION_TITLES } from './buildMeasureSections'

// Generic, catalog-aware measure picker dropdown used by stack-mode
// chart tools (Scatter axes, Allocation measure, Trend `+ Compare`).
//
// Behavior:
//   - Trigger button shows the current label + chevron. Variant
//     'select' = filled-rect select look (Scatter / Allocation),
//     'chip'   = pill/button look (Trend "+ Compare").
//   - Click opens a Popover containing the sectioned options laid
//     out as TWO COLUMNS: "In this view" anchors the left column,
//     "Suggested" + "Added" stack in the right. Empty sections are
//     collapsed; if only one column has content the popover snaps
//     to a single-column grid.
//   - Each option is a real <button> (focus-visible outline,
//     keyboard navigable). An option whose `id === excludeId` is
//     rendered disabled (mutual-exclusion case in Scatter where
//     one axis can't equal the other). An option carrying
//     `disabledReason` is also rendered disabled with the reason
//     in a tooltip (Trend compatibility).
//   - Optional `onMore` callback adds a "+ More measures..." footer
//     item; clicking it closes the popover and fires `onMore` so
//     the caller can mount a MeasurePickerDialog.

const useStyles = makeStyles(() => ({
  triggerSelect: {
    appearance: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    minWidth: 150,
    padding: '4px 8px',
    background: 'var(--opal-surface-default, #fff)',
    border: '1px solid var(--color-neutral-300, #d1d5db)',
    borderRadius: 'var(--border-radius-sm, 4px)',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 12,
    color: 'var(--color-neutral-800, #222)',
    '&:hover': {
      borderColor: 'var(--color-primary-400, #3a83f5)'
    },
    '&:focus-visible': {
      outline: '2px solid var(--color-primary-400, #3a83f5)',
      outlineOffset: 1
    }
  },
  triggerChip: {
    appearance: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    height: 24,
    minHeight: 24,
    padding: '0 10px',
    background: 'transparent',
    border: '1px solid var(--opal-border-default, #e0e0e0)',
    borderRadius: 12,
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 12,
    color: 'var(--color-primary-700, var(--color-neutral-800, #333))',
    '&:hover': {
      borderColor: 'var(--color-primary-400, #3a83f5)',
      background: 'var(--color-primary-50, #f0f7ff)'
    },
    '&:focus-visible': {
      outline: '2px solid var(--color-primary-400, #3a83f5)',
      outlineOffset: 1
    },
    '&:disabled': {
      cursor: 'default',
      opacity: 0.55,
      borderColor: 'var(--opal-border-default, #e0e0e0)',
      background: 'transparent'
    }
  },
  triggerLabel: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textAlign: 'left'
  },
  chevron: {
    fontSize: 10,
    opacity: 0.7
  },
  popoverPaper: {
    padding: 0,
    overflow: 'hidden'
  },
  columns: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(160px, 1fr))',
    gridAutoRows: 'min-content',
    columnGap: 0,
    padding: '4px 0'
  },
  columnsSingle: {
    gridTemplateColumns: 'minmax(160px, 1fr)'
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    padding: '0 4px'
  },
  subheader: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: 'var(--color-neutral-500, #888)',
    lineHeight: 1,
    padding: '8px 12px 4px',
    background: 'transparent'
  },
  item: {
    display: 'block',
    width: '100%',
    padding: '6px 12px',
    background: 'transparent',
    border: 'none',
    borderRadius: 'var(--border-radius-sm, 4px)',
    fontFamily: 'inherit',
    fontSize: 13,
    textAlign: 'left',
    color: 'var(--color-neutral-900, #111827)',
    cursor: 'pointer',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    '&:hover': {
      background: 'var(--color-primary-50, #f0f7ff)'
    },
    '&:focus-visible': {
      outline: '2px solid var(--color-primary-400, #3a83f5)',
      outlineOffset: -2
    }
  },
  itemSelected: {
    background: 'var(--color-primary-100, #d3e3fd)',
    fontWeight: 'var(--font-weight-semibold, 600)',
    '&:hover': {
      background: 'var(--color-primary-100, #d3e3fd)'
    }
  },
  itemDisabled: {
    color: 'var(--color-neutral-400, #9ca3af)',
    cursor: 'default',
    '&:hover': {
      background: 'transparent'
    }
  },
  emptyMessage: {
    padding: '12px 16px',
    fontSize: 12,
    color: 'var(--color-neutral-500, #6b7280)',
    fontStyle: 'italic'
  },
  moreItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    width: '100%',
    padding: '8px 12px',
    background: 'transparent',
    border: 'none',
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: 'var(--font-weight-medium, 500)',
    color: 'var(--color-primary-700, #1557b0)',
    textAlign: 'left',
    cursor: 'pointer',
    '&:hover': {
      background: 'var(--color-primary-50, #f0f7ff)'
    },
    '&:focus-visible': {
      outline: '2px solid var(--color-primary-400, #3a83f5)',
      outlineOffset: -2
    }
  }
}))

function SectionBlock ({ section, currentId, excludeId, onPick, classes }) {
  if (!section?.options?.length) return null
  return (
    <>
      {section.title && (
        <div className={classes.subheader}>{section.title}</div>
      )}
      {section.options.map(opt => {
        const disabledByExclusion = opt.id === excludeId
        const disabledByReason = !!opt.disabledReason
        const disabled = disabledByExclusion || disabledByReason
        const selected = opt.id === currentId
        const cls = [
          classes.item,
          selected ? classes.itemSelected : '',
          disabled ? classes.itemDisabled : ''
        ].filter(Boolean).join(' ')
        const button = (
          <button
            key={opt.id}
            type='button'
            className={cls}
            disabled={disabled}
            aria-pressed={selected}
            onClick={() => onPick(opt.id)}
            title={disabledByReason ? undefined : opt.label}
          >
            {opt.label}
          </button>
        )
        if (disabledByReason) {
          // MUI swallows tooltips on disabled buttons; wrap in a span.
          return (
            <Tooltip key={opt.id} title={opt.disabledReason} placement='right' enterDelay={400}>
              <span style={{ display: 'block' }}>{button}</span>
            </Tooltip>
          )
        }
        return button
      })}
    </>
  )
}

SectionBlock.propTypes = {
  section: PropTypes.shape({
    title: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      disabledReason: PropTypes.string
    }))
  }),
  currentId: PropTypes.string,
  excludeId: PropTypes.string,
  onPick: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired
}

// "In this view" anchors the left column; "Suggested" and "Added"
// stack in the right column so reading flows have -> offered ->
// added. When only one column has content we collapse to a single
// column so the popover isn't awkwardly wide. Unknown section
// titles drop into the right column under "Suggested".
function organizeSections (sections) {
  const byTitle = new Map((sections || []).map(s => [s.title, s]))
  const left = byTitle.get(MEASURE_SECTION_TITLES.view) || { title: MEASURE_SECTION_TITLES.view, options: [] }
  const rightOrdered = [MEASURE_SECTION_TITLES.suggested, MEASURE_SECTION_TITLES.added]
    .map(title => byTitle.get(title))
    .filter(s => s && s.options.length > 0)
  return { left, right: rightOrdered }
}

function defaultRenderTrigger ({
  variant,
  classes,
  triggerLabel,
  placeholder,
  isOpen,
  onOpen,
  disabled,
  ariaLabel
}) {
  const className = variant === 'chip' ? classes.triggerChip : classes.triggerSelect
  return (
    <button
      type='button'
      className={className}
      onClick={onOpen}
      disabled={disabled}
      aria-haspopup='listbox'
      aria-expanded={isOpen}
      aria-label={ariaLabel}
    >
      <span className={classes.triggerLabel}>{triggerLabel || placeholder}</span>
      <span className={classes.chevron} aria-hidden='true'>▾</span>
    </button>
  )
}

export default function MeasureDropdown ({
  sections,
  currentId,
  excludeId,
  onChange,
  onMore,
  moreLabel = 'More measures…',
  variant = 'select',
  triggerLabel,
  triggerPlaceholder = '— pick —',
  triggerAriaLabel,
  triggerDisabled = false,
  emptyMessage = 'No measures available'
}) {
  const classes = useStyles()
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const handleOpen = useCallback((e) => setAnchorEl(e.currentTarget), [])
  const handleClose = useCallback(() => setAnchorEl(null), [])
  const handlePick = useCallback((id) => {
    handleClose()
    onChange(id)
  }, [onChange, handleClose])
  const handleMore = useCallback(() => {
    handleClose()
    if (onMore) onMore()
  }, [onMore, handleClose])

  const { left, right } = organizeSections(sections)
  const showLeftColumn = left.options.length > 0
  const showRightColumn = right.length > 0
  const singleColumn = !showLeftColumn || !showRightColumn
  const hasAnyContent = showLeftColumn || showRightColumn

  return (
    <>
      {defaultRenderTrigger({
        variant,
        classes,
        triggerLabel,
        placeholder: triggerPlaceholder,
        isOpen: open,
        onOpen: handleOpen,
        disabled: triggerDisabled,
        ariaLabel: triggerAriaLabel
      })}
      <Popover
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        classes={{ paper: classes.popoverPaper }}
      >
        {!hasAnyContent && (
          <div className={classes.emptyMessage}>{emptyMessage}</div>
        )}
        {hasAnyContent && (
          <div className={`${classes.columns} ${singleColumn ? classes.columnsSingle : ''}`}>
            {showLeftColumn && (
              <div className={classes.column}>
                <SectionBlock
                  section={left}
                  currentId={currentId}
                  excludeId={excludeId}
                  onPick={handlePick}
                  classes={classes}
                />
              </div>
            )}
            {showRightColumn && (
              <div className={classes.column}>
                {right.map(section => (
                  <SectionBlock
                    key={section.title}
                    section={section}
                    currentId={currentId}
                    excludeId={excludeId}
                    onPick={handlePick}
                    classes={classes}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        {onMore && (
          <>
            {hasAnyContent && <Divider />}
            <button type='button' className={classes.moreItem} onClick={handleMore}>
              <AddIcon style={{ fontSize: 16 }} />
              {moreLabel}
            </button>
          </>
        )}
      </Popover>
    </>
  )
}

MeasureDropdown.propTypes = {
  // [{ title, options: [{ id, label, disabledReason? }] }]. Recommended:
  // use `buildMeasureSections({ measureOptions, viewIds, suggestedIds,
  // userIds })` to construct.
  sections: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      disabledReason: PropTypes.string
    })).isRequired
  })).isRequired,
  currentId: PropTypes.string,
  // For mutual-exclusion cases (Scatter X disables Y's current value).
  excludeId: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onMore: PropTypes.func,
  moreLabel: PropTypes.string,
  variant: PropTypes.oneOf(['select', 'chip']),
  // Label shown in the trigger button. Caller usually derives from
  // `sections.flatMap(s => s.options).find(o => o.id === currentId).label`.
  triggerLabel: PropTypes.string,
  triggerPlaceholder: PropTypes.string,
  triggerAriaLabel: PropTypes.string,
  triggerDisabled: PropTypes.bool,
  emptyMessage: PropTypes.string
}
